import {getHrefForLinkType, getRelAttributes, getTargetAttribute} from 'Application/utils/linkFieldUtils';
import {isDefined} from 'Application/utils/typeGuards';

import type {CtaBlockType} from 'types/payload-types';

/**
 * The `useCtaBlock` hook processes CTA (Call-to-Action) button link data and extracts relevant properties for rendering.
 * This hook takes a CTA button link object and returns formatted href, rel, target, and text properties that can be used directly in anchor elements.
 * It handles undefined or null link objects gracefully by returning empty default values.
 *
 * @param link The CTA button link object containing link type, URL, and label information.
 * @returns An object containing processed link properties (href, rel, target, text) ready for use in JSX.
 *
 * @example
 * ```tsx
 * const ctaLink = {
 *   type: 'custom',
 *   url: 'https://example.com',
 *   label: 'Click here',
 *   newTab: true
 * };
 *
 * const { ctaHref, ctaRel, ctaTarget, ctaText } = useCtaBlock(ctaLink);
 *
 * return (
 *   <a href={ctaHref} rel={ctaRel} target={ctaTarget}>
 *     {ctaText}
 *   </a>
 * );
 * ```
 */
export const useCtaBlock = (link: CtaBlockType['ctaButton']) => {
    let ctaHref = '';
    let ctaRel;
    let ctaTarget;
    let ctaText = '';

    if (isDefined(link)) {
        ctaHref = getHrefForLinkType(link);
        ctaRel = getRelAttributes(link);
        ctaTarget = getTargetAttribute(link);
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        ctaText = link.label ?? '';
    }

    return {
        ctaHref,
        ctaRel,
        ctaTarget,
        ctaText
    };
};